import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { adminService } from '../../services/adminService';
import ComponentEditModal from '../../components/admin/ComponentEditModal';
import './AdminComponents.css';

const AdminComponents = () => {
  const { type } = useParams();
  const componentType = type || 'bases';

  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingComponent, setEditingComponent] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchComponents();
  }, [componentType]);

  const fetchComponents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getComponents(componentType);
      if (response.success) {
        setComponents(response.data || []);
      } else {
        setError(response.message || 'Failed to load components');
      }
    } catch (err) {
      console.error('Error fetching components:', err);
      setError('Failed to load components');
    } finally {
      setLoading(false);
    }
  };

  const activeComponents = components.filter((c) => c.isActive !== false);
  const inactiveComponents = components.filter((c) => c.isActive === false);

  const handleEdit = (component) => {
    setEditingComponent(component);
  };

  const handleToggleActive = async (component) => {
    try {
      const requestData = {
        productComponentName: component.productComponentName,
        productComponentCode: component.productComponentCode,
        description: component.description || '',
        isActive: component.isActive === false,
        price: component.price != null ? component.price : null,
        attributes: component.attributes || {},
      };
      const response = await adminService.updateComponent(component.id, requestData);
      if (response.success) {
        fetchComponents();
      } else {
        alert(response.message || 'Failed to update component');
      }
    } catch (err) {
      console.error('Error toggling component:', err);
      alert('Failed to update component');
    }
  };

  const handleDelete = async (componentId) => {
    try {
      const response = await adminService.deleteComponent(componentId);
      if (response.success) {
        setEditingComponent(null);
        fetchComponents();
      } else {
        alert(response.message || 'Failed to delete component');
      }
    } catch (err) {
      console.error('Error deleting component:', err);
      alert('Failed to delete component');
    }
  };

  const handleSave = async (componentData) => {
    try {
      const requestData = {
        productComponentName: componentData.name,
        productComponentCode: componentData.code,
        description: componentData.description || '',
        isActive: componentData.isActive !== false,
        price: componentData.price != null ? componentData.price : null,
        attributes: {
          ...(componentData.modelPath && { modelPath: componentData.modelPath }),
          ...(componentData.thumbnailUrl && { thumbnailUrl: componentData.thumbnailUrl }),
          ...(componentData.hexCode && { hexCode: componentData.hexCode }),
        },
      };

      let response;
      if (editingComponent) {
        // Update existing
        response = await adminService.updateComponent(editingComponent.id, requestData);
      } else {
        // Create new
        response = await adminService.createComponent(componentType, requestData);
      }

      if (response.success) {
        setEditingComponent(null);
        setShowAddModal(false);
        fetchComponents(); // Refresh list
      } else {
        alert(response.message || 'Failed to save component');
      }
    } catch (err) {
      console.error('Error saving component:', err);
      alert('Failed to save component');
    }
  };

  const tabs = [
    { id: 'bases', label: 'Bases' },
    { id: 'shades', label: 'Shades' },
    { id: 'colors', label: 'Colors' },
  ];

  if (loading) {
    return (
      <div className="admin-components">
        <div className="admin-loading">Loading components...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-components">
        <div className="admin-error">
          <p>{error}</p>
          <button onClick={fetchComponents} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-components">
      <div className="components-header">
        <div className="components-tabs">
          {tabs.map((tab) => (
            <Link
              key={tab.id}
              to={`/admin/components/${tab.id}`}
              className={`tab ${componentType === tab.id ? 'active' : ''}`}
            >
              {tab.label}
            </Link>
          ))}
        </div>
        <button className="add-btn" onClick={() => setShowAddModal(true)}>
          + Add New{' '}
          {componentType === 'bases'
            ? 'Base'
            : componentType === 'shades'
            ? 'Shade'
            : 'Color'}
        </button>
      </div>

      {/* Active Components */}
      <div className="components-section">
        <h3 className="section-title">
          Active Components ({activeComponents.length})
        </h3>
        {activeComponents.length === 0 ? (
          <div className="no-components">No active components</div>
        ) : (
          <div className="components-grid">
            {activeComponents.map((component) => (
              <ComponentCard
                key={component.id}
                component={component}
                type={componentType}
                onEdit={handleEdit}
                onToggleActive={handleToggleActive}
              />
            ))}
          </div>
        )}
      </div>

      {/* Inactive Components */}
      {inactiveComponents.length > 0 && (
        <div className="components-section">
          <h3 className="section-title inactive">
            Inactive Components ({inactiveComponents.length})
          </h3>
          <div className="components-grid">
            {inactiveComponents.map((component) => (
              <ComponentCard
                key={component.id}
                component={component}
                type={componentType}
                onEdit={handleEdit}
                onToggleActive={handleToggleActive}
                isInactive
              />
            ))}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <AnimatePresence>
        {editingComponent && (
          <ComponentEditModal
            component={editingComponent}
            type={componentType}
            onClose={() => setEditingComponent(null)}
            onSave={handleSave}
            onDelete={handleDelete}
          />
        )}
      </AnimatePresence>

      {/* Add Modal */}
      <AnimatePresence>
        {showAddModal && (
          <ComponentEditModal
            component={null}
            type={componentType}
            onClose={() => setShowAddModal(false)}
            onSave={handleSave}
          />
        )}
      </AnimatePresence>

    </div>
  );
};

const ComponentCard = ({
  component,
  type,
  onEdit,
  onToggleActive,
  isInactive,
}) => {
  // Map API response fields to UI fields
  const displayData = {
    name: component.productComponentName || component.name,
    code: component.productComponentCode || component.code,
    thumbnailUrl:
      component.attributes?.thumbnailUrl || component.thumbnailUrl,
    modelPath: component.attributes?.modelPath || component.modelPath,
    hexCode: component.attributes?.hexCode || component.hexCode,
    price: component.price,
  };

  // Format price for display
  const formatPrice = (price) => {
    if (price == null || price === 0) return null;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <motion.div
      className={`component-card ${isInactive ? 'inactive' : ''}`}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
    >
      {type !== 'colors' && (
        <div className="component-image">
          <img
            src={displayData.thumbnailUrl || '/images/placeholder.png'}
            alt={displayData.name}
            onError={(e) => {
              e.target.src = '/images/placeholder.png';
            }}
          />
        </div>
      )}

      {type === 'colors' && (
        <div
          className="color-swatch"
          style={{ backgroundColor: displayData.hexCode || '#ccc' }}
        />
      )}

      <div className="component-info">
        <div className="component-name">{displayData.name}</div>
        <div className="component-code">{displayData.code}</div>
        {displayData.price != null && (
          <div className="component-price">
            {type === 'colors' ? (
              displayData.price > 0 ? `+${formatPrice(displayData.price)}` : 'No premium'
            ) : (
              formatPrice(displayData.price) || 'Price not set'
            )}
          </div>
        )}
        {isInactive && <div className="inactive-badge">Inactive</div>}
      </div>

      <div className="component-actions">
        <button
          className="action-btn edit-btn"
          onClick={() => onEdit(component)}
        >
          Edit
        </button>
        <button
          className={`action-btn ${isInactive ? 'restore-btn' : 'disable-btn'}`}
          onClick={() => onToggleActive(component)}
        >
          {isInactive ? 'Activate' : 'Disable'}
        </button>
      </div>
    </motion.div>
  );
};

export default AdminComponents;
